import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    PiArrowsClockwiseBold,
    PiBuildingsBold,
    PiCalendarBlankBold,
    PiCurrencyCircleDollarBold,
    PiHandshakeBold,
    PiReceiptBold,
    PiUserBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

type BillingKpis = {
    deal_volume: number;
    commission_paid: number;
    installments_paid: number;
    effective_rate_pct: number;
};
type BillingMonthly = {
    labels: string[];
    dealVolume: number[];
    dealCount: number[];
    commissionPaid: number[];
    installmentsPaid: number[];
};
type BillingBySeller = { user_id: number; seller_name: string; commission_paid: number; deal_volume: number; deal_count: number; effective_rate: number }[];
type BillingByYear = { year: number; deal_volume: number; commission_paid: number; installments_paid: number }[];
type BillingBySupplier = { supplier: string; deal_volume: number }[];

type BillingReport = {
    year: number;
    available_years: number[];
    kpis: BillingKpis;
    monthly: BillingMonthly;
    by_year: BillingByYear;
    by_seller: BillingBySeller;
    by_supplier: BillingBySupplier;
};

export default function BillingReportSection() {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [activeTab, setActiveTab] = useState<'monthly' | 'sellers' | 'suppliers' | 'annual'>('monthly');

    const { data, isLoading } = useQuery<BillingReport>({
        queryKey: ['billing-report', year],
        queryFn: async () => {
            const res = await api.get('/commissioning/billing-report', { params: { year } });
            return res.data.data;
        },
    });

    const availableYears = data?.available_years ?? [currentYear];

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-base font-black text-gray-900 dark:text-zinc-100">Faturamento Global</h2>
                    <p className="text-xs text-gray-500 dark:text-zinc-500">Volume de vendas, comissões pagas e recebimentos de clientes</p>
                </div>
                <div className="flex items-center gap-2">
                    {isLoading && <PiArrowsClockwiseBold className="size-4 animate-spin text-gray-400" />}
                    <select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-800 outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"
                    >
                        {availableYears.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* KPI cards */}
            {data && (
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <BillingKpiCard
                        icon={PiHandshakeBold}
                        label="Total Vendido"
                        value={brl(data.kpis.deal_volume)}
                        sub={`${year}`}
                        color="blue"
                    />
                    <BillingKpiCard
                        icon={PiReceiptBold}
                        label="Comissões Pagas"
                        value={brl(data.kpis.commission_paid)}
                        sub={`${data.kpis.effective_rate_pct}% do volume`}
                        color="emerald"
                    />
                    <BillingKpiCard
                        icon={PiCurrencyCircleDollarBold}
                        label="Recebimentos (Cliente)"
                        value={brl(data.kpis.installments_paid)}
                        sub="parcelas pagas"
                        color="violet"
                    />
                    <BillingKpiCard
                        icon={PiCalendarBlankBold}
                        label="Taxa Efetiva"
                        value={`${data.kpis.effective_rate_pct}%`}
                        sub="comissão / vendas"
                        color="amber"
                    />
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-zinc-700 dark:bg-zinc-800/50">
                {([
                    { key: 'monthly', label: 'Mensal' },
                    { key: 'annual', label: 'Por Ano' },
                    { key: 'sellers', label: 'Por Vendedor' },
                    { key: 'suppliers', label: 'Por Fornecedor' },
                ] as const).map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={cn(
                            'flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition',
                            activeTab === tab.key
                                ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                                : 'text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200',
                        )}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            {data && activeTab === 'monthly' && <MonthlyTable data={data.monthly} />}
            {data && activeTab === 'annual' && <AnnualTable rows={data.by_year} />}
            {data && activeTab === 'sellers' && <SellersTable rows={data.by_seller} />}
            {data && activeTab === 'suppliers' && <SuppliersTable rows={data.by_supplier} />}
        </div>
    );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function BillingKpiCard({ icon: Icon, label, value, sub, color }: {
    icon: React.ElementType; label: string; value: string; sub?: string;
    color: 'blue' | 'emerald' | 'violet' | 'amber';
}) {
    const colors: Record<string, string> = {
        blue: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400',
        emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
        violet: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400',
        amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
    };
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <div className={cn('mb-2 inline-flex size-8 items-center justify-center rounded-lg', colors[color])}>
                <Icon className="size-4" />
            </div>
            <p className="text-xs text-gray-500 dark:text-zinc-500">{label}</p>
            <p className="text-xl font-black text-gray-900 dark:text-zinc-100">{value}</p>
            {sub && <p className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-600">{sub}</p>}
        </div>
    );
}

// ─── Monthly Table ─────────────────────────────────────────────────────────────

function MonthlyTable({ data }: { data: BillingMonthly }) {
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <table className="min-w-full text-xs">
                <thead className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-500">
                    <tr>
                        <th className="px-4 py-2.5 text-left">Mês</th>
                        <th className="px-4 py-2.5 text-right">Deals</th>
                        <th className="px-4 py-2.5 text-right">Volume Vendido</th>
                        <th className="px-4 py-2.5 text-right">Comissões Pagas</th>
                        <th className="px-4 py-2.5 text-right">Recebimentos</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                    {data.labels.map((ym, i) => {
                        const [, m] = ym.split('-');
                        return (
                            <tr key={ym} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                                <td className="px-4 py-2.5 font-semibold text-gray-700 dark:text-zinc-300">
                                    {MONTH_LABELS[parseInt(m) - 1]}
                                </td>
                                <td className="px-4 py-2.5 text-right text-gray-500">{data.dealCount[i] || '—'}</td>
                                <td className="px-4 py-2.5 text-right font-mono text-blue-700 dark:text-blue-400">
                                    {data.dealVolume[i] > 0 ? brl(data.dealVolume[i]) : '—'}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono text-emerald-700 dark:text-emerald-400">
                                    {data.commissionPaid[i] > 0 ? brl(data.commissionPaid[i]) : '—'}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono text-violet-700 dark:text-violet-400">
                                    {data.installmentsPaid[i] > 0 ? brl(data.installmentsPaid[i]) : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot className="border-t border-gray-200 bg-gray-50 font-semibold dark:border-zinc-700 dark:bg-zinc-800/50">
                    <tr>
                        <td className="px-4 py-2.5 text-xs font-black text-gray-700 dark:text-zinc-300">Total</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{data.dealCount.reduce((a, b) => a + b, 0)}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-black text-blue-700 dark:text-blue-400">
                            {brl(data.dealVolume.reduce((a, b) => a + b, 0))}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-black text-emerald-700 dark:text-emerald-400">
                            {brl(data.commissionPaid.reduce((a, b) => a + b, 0))}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-black text-violet-700 dark:text-violet-400">
                            {brl(data.installmentsPaid.reduce((a, b) => a + b, 0))}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

// ─── Annual Table ──────────────────────────────────────────────────────────────

function AnnualTable({ rows }: { rows: BillingByYear }) {
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <table className="min-w-full text-xs">
                <thead className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-500">
                    <tr>
                        <th className="px-4 py-2.5 text-left">Ano</th>
                        <th className="px-4 py-2.5 text-right">Volume Vendido</th>
                        <th className="px-4 py-2.5 text-right">Comissões Pagas</th>
                        <th className="px-4 py-2.5 text-right">Recebimentos</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                    {rows.map((r) => (
                        <tr key={r.year} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                            <td className="px-4 py-2.5 font-black text-gray-900 dark:text-zinc-100">{r.year}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-blue-700 dark:text-blue-400">{brl(r.deal_volume)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-emerald-700 dark:text-emerald-400">{brl(r.commission_paid)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-violet-700 dark:text-violet-400">{brl(r.installments_paid)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Sellers Table ─────────────────────────────────────────────────────────────

function SellersTable({ rows }: { rows: BillingBySeller }) {
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <table className="min-w-full text-xs">
                <thead className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-500">
                    <tr>
                        <th className="px-4 py-2.5 text-left">Vendedor</th>
                        <th className="px-4 py-2.5 text-right">Deals</th>
                        <th className="px-4 py-2.5 text-right">Volume Vendido</th>
                        <th className="px-4 py-2.5 text-right">Comissão Paga</th>
                        <th className="px-4 py-2.5 text-right">% Efetiva</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                    {rows.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Nenhum dado para este período.</td></tr>
                    )}
                    {rows.map((r) => (
                        <tr key={r.user_id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                            <td className="px-4 py-2.5 font-semibold text-gray-800 dark:text-zinc-200">
                                <div className="flex items-center gap-2">
                                    <div className="flex size-6 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-700">
                                        <PiUserBold className="size-3 text-gray-500" />
                                    </div>
                                    {r.seller_name}
                                </div>
                            </td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{r.deal_count}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-blue-700 dark:text-blue-400">{brl(r.deal_volume)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-emerald-700 dark:text-emerald-400">{brl(r.commission_paid)}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-amber-700 dark:text-amber-400">{r.effective_rate}%</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Suppliers Table ───────────────────────────────────────────────────────────

function SuppliersTable({ rows }: { rows: BillingBySupplier }) {
    const total = rows.reduce((acc, r) => acc + r.deal_volume, 0);
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <table className="min-w-full text-xs">
                <thead className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-500">
                    <tr>
                        <th className="px-4 py-2.5 text-left">Fornecedor</th>
                        <th className="px-4 py-2.5 text-right">Volume Vendido</th>
                        <th className="px-4 py-2.5 text-right">% do Total</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                    {rows.length === 0 && (
                        <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">Nenhum dado para este período.</td></tr>
                    )}
                    {rows.map((r) => (
                        <tr key={r.supplier} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                            <td className="px-4 py-2.5 font-semibold text-gray-800 dark:text-zinc-200">
                                <div className="flex items-center gap-2">
                                    <PiBuildingsBold className="size-3.5 shrink-0 text-gray-400" />
                                    {r.supplier}
                                </div>
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono text-blue-700 dark:text-blue-400">{brl(r.deal_volume)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">
                                {total > 0 ? `${Math.round(r.deal_volume / total * 100)}%` : '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
